import { Form } from '../Components/Form';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { classNames } from '../Utils/utils';
import {
    ServicePackage,
    ServiceSlot,
    ServiceSlotAvailabilityIndex,
    ServiceSlotDayKey,
    ServiceSlotDaySlots,
    StepProps,
} from '../../types';
import { useTranslation } from 'react-i18next';

export default function ServiceSlotSelection({ form, setForm, onStepCompleted, setIsLoading, isLoading, navigateToStep }: StepProps) {
    const { t } = useTranslation();
    const servicePackageCode: ServicePackage['code'] = form.service_package_code!;

    const [serviceSlots, setServiceSlots] = useState<ServiceSlot[]>([]);
    const [selectedDay, setSelectedDay] = useState<string | null>(null);
    const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

    const { byDay, allDaysSorted } = useMemo(() => buildAvailabilityIndex(serviceSlots), [serviceSlots]);

    useEffect(() => {
        let isMounted = true;

        (async () => {
            setIsLoading(true);
            try {
                const slots = await api.getServiceSlots(form.standort_betriebsnummer!, servicePackageCode, form.fahrzeug.fabrikat_code);
                if (isMounted) {
                    setServiceSlots(slots);
                }
            } catch (e) {
                if (isMounted) {
                    console.error(e);
                    alert(t('appointment.loadError'));
                    navigateToStep(2);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            isMounted = false;
        };
    }, []);

    function selectSlot(id: string) {
        setSelectedSlotId(id);

        const selectedSlotIndex = selectedDay ? byDay[selectedDay]?.slots.find((s) => s.id === id)?.rawIndex : null;

        const hasValidIndex = selectedSlotIndex !== null && selectedSlotIndex !== undefined;

        setForm((f) => ({
            ...f,
            servicetermin: (hasValidIndex ? serviceSlots[selectedSlotIndex!] : null) as ServiceSlot,
        }));
    }

    const handleSubmit = (e: any) => {
        e.preventDefault();
        // Form submission handled by card clicks
    };

    if (isLoading) {
        return <div className={'flex justify-center items-center h-full'}>{t('appointment.loading')}</div>;
    }

    const currentSlots = selectedDay ? byDay[selectedDay]?.slots ?? [] : [];

    return (
        <Form className="mx-auto max-w-5xl" onSubmit={handleSubmit}>
            <MonthCalendar
                byDay={byDay}
                setSelectedDay={setSelectedDay}
                selectedDay={selectedDay}
                allDaysSorted={allDaysSorted}
                setSelectedSlotId={setSelectedSlotId}
            />

            {selectedDay && currentSlots.length > 0 && (
                <div className="mt-8">
                    <GroupedCardVariation1
                        slots={currentSlots}
                        selectedSlotId={selectedSlotId}
                        onSelectSlot={(id) => {
                            selectSlot(id);
                            onStepCompleted();
                        }}
                    />
                </div>
            )}
        </Form>
    );
}

// Helper function to group slots by Vormittag/Nachmittag
function groupSlotsByPeriod(slots: ServiceSlotDaySlots['slots']) {
    const groups: {
        morning: typeof slots;
        afternoon: typeof slots;
    } = {
        morning: [],
        afternoon: [],
    };

    slots.forEach((slot) => {
        const [hour] = slot.start.split(':').map(Number);
        if (hour < 12) {
            groups.morning.push(slot);
        } else {
            groups.afternoon.push(slot);
        }
    });

    return groups;
}

// Card component used in all variations (no rounded corners)
function TimeSlotCard({
    slot,
    isSelected,
    onSelect,
}: {
    slot: ServiceSlotDaySlots['slots'][0];
    isSelected: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            className={classNames(
                'border px-4 py-3 text-left transition-all text-lg font-semibold',
                isSelected
                    ? 'border-primary bg-primary text-white shadow-md'
                    : 'border-secondary-500 bg-white shadow-sm hover:border-primary hover:shadow-md'
            )}
            onClick={onSelect}
        >
            {slot.start}
        </button>
    );
}

// Variation 1: Collapsible Sections with Cards
function GroupedCardVariation1({
    slots,
    selectedSlotId,
    onSelectSlot,
}: {
    slots: ServiceSlotDaySlots['slots'];
    selectedSlotId: string | null;
    onSelectSlot: (id: string) => void;
}) {
    const { t } = useTranslation();
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
        morning: true,
        afternoon: true,
    });

    const groupedSlots = useMemo(() => groupSlotsByPeriod(slots), [slots]);

    const toggleGroup = (group: string) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [group]: !prev[group],
        }));
    };

    const renderGroup = (
        label: string,
        groupKey: 'morning' | 'afternoon',
        groupSlots: typeof slots
    ) => {
        if (groupSlots.length === 0) return null;

        const isExpanded = expandedGroups[groupKey];

        return (
            <div className="mb-4 border border-secondary-500">
                <button
                    type="button"
                    onClick={() => toggleGroup(groupKey)}
                    className="flex w-full items-center justify-between border-b border-secondary-500 px-4 py-3 text-left hover:bg-secondary-600"
                >
                    <span className="font-semibold text-secondary-200">{label}</span>
                    <svg
                        className={classNames(
                            'h-5 w-5 text-secondary-300 transition-transform',
                            isExpanded ? 'rotate-180' : ''
                        )}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                {isExpanded && (
                    <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
                        {groupSlots.map((slot) => (
                            <TimeSlotCard
                                key={slot.id}
                                slot={slot}
                                isSelected={slot.id === selectedSlotId}
                                onSelect={() => onSelectSlot(slot.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            {renderGroup(t('appointment.morning'), 'morning', groupedSlots.morning)}
            {renderGroup(t('appointment.afternoon'), 'afternoon', groupedSlots.afternoon)}
        </div>
    );
}

function parseDateKey(value: string) {
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
}

function formatDateKey(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}


// Month view calendar for date selection
function MonthCalendar({
    byDay,
    setSelectedDay,
    selectedDay,
    allDaysSorted,
    setSelectedSlotId,
}: {
    byDay: Record<string, ServiceSlotDaySlots>;
    setSelectedDay: (value: ((prevState: string | null) => string | null) | string | null) => void;
    selectedDay: string | null;
    allDaysSorted: ServiceSlotDayKey[];
    setSelectedSlotId: (value: ((prevState: string | null) => string | null) | string | null) => void;
}) {
    // Determine initial month based on first available day
    const initialMonth = useMemo(() => {
        if (allDaysSorted.length > 0) {
            const firstDate = parseDateKey(allDaysSorted[0]);
            if (firstDate) {
                return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
            }
        }
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    }, [allDaysSorted]);

    const [currentMonth, setCurrentMonth] = useState(initialMonth);

    useEffect(() => {
        if (allDaysSorted.length === 0) return;
        const firstDate = parseDateKey(allDaysSorted[0]);
        if (!firstDate) return;
        setCurrentMonth((prev) => {
            if (prev.year === firstDate.getFullYear() && prev.month === firstDate.getMonth()) {
                return prev;
            }
            return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
        });
    }, [allDaysSorted]);

    // Build calendar grid for the current month
    const calendarGrid = useMemo(() => {
        const { year, month } = currentMonth;
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);

        // Start from Monday of the week containing the 1st
        const startDay = (firstDayOfMonth.getDay() + 6) % 7; // 0 = Monday
        const startDate = new Date(firstDayOfMonth);
        startDate.setDate(1 - startDay);

        // Build 6 weeks of dates (42 days) to ensure full month coverage
        const days: string[] = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(startDate);
            d.setDate(startDate.getDate() + i);
            days.push(formatDateKey(d));
        }

        return days;
    }, [currentMonth]);

    const { t } = useTranslation();
    const monthName = new Date(currentMonth.year, currentMonth.month, 1).toLocaleDateString('de-DE', {
        month: 'long',
        year: 'numeric',
    });

    const weekDays = [
        t('weekday.mon'),
        t('weekday.tue'),
        t('weekday.wed'),
        t('weekday.thu'),
        t('weekday.fri'),
        t('weekday.sat'),
        t('weekday.sun'),
    ];

    const goToPrevMonth = () => {
        setCurrentMonth((prev) => {
            if (prev.month === 0) {
                return { year: prev.year - 1, month: 11 };
            }
            return { year: prev.year, month: prev.month - 1 };
        });
    };

    const goToNextMonth = () => {
        setCurrentMonth((prev) => {
            if (prev.month === 11) {
                return { year: prev.year + 1, month: 0 };
            }
            return { year: prev.year, month: prev.month + 1 };
        });
    };

    const handleDayClick = (day: string) => {
        setSelectedSlotId(null);
        setSelectedDay(day);
    };

    return (
        <div className="mx-auto max-w-md">
            {/* Month navigation header */}
            <div className="mb-4 flex items-center justify-between">
                <button
                    type="button"
                    onClick={goToPrevMonth}
                    className="p-2 text-secondary-300 transition-colors hover:text-secondary-100"
                >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h3 className="text-lg font-semibold text-secondary-100 capitalize">{monthName}</h3>
                <button
                    type="button"
                    onClick={goToNextMonth}
                    className="p-2 text-secondary-300 transition-colors hover:text-secondary-100"
                >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {/* Weekday headers */}
            <div className="mb-2 grid grid-cols-7 gap-1">
                {weekDays.map((day) => (
                    <div key={day} className="py-2 text-center text-xs font-semibold uppercase text-secondary-300">
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
                {calendarGrid.map((day) => {
                    const date = parseDateKey(day) || new Date(day);
                    const isCurrentMonth = date.getMonth() === currentMonth.month;
                    const hasSlots = (byDay[day]?.slots?.length || 0) > 0;
                    const isSelected = day === selectedDay;
                    const isToday = day === formatDateKey(new Date());

                    return (
                        <button
                            key={day}
                            type="button"
                            onClick={() => hasSlots && handleDayClick(day)}
                            disabled={!hasSlots}
                            className={classNames(
                                'flex h-10 w-full items-center justify-center text-sm font-medium transition-all',
                                // Selected state
                                isSelected && 'bg-primary text-white',
                                // Has slots but not selected
                                hasSlots && !isSelected && 'cursor-pointer bg-primary/10 text-primary hover:bg-primary hover:text-white',
                                // No slots available
                                !hasSlots && isCurrentMonth && 'cursor-not-allowed text-secondary-500',
                                // Not current month
                                !isCurrentMonth && 'text-secondary-600',
                                // Today indicator
                                isToday && !isSelected && 'ring-1 ring-primary'
                            )}
                        >
                            {date.getDate()}
                        </button>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="mt-4 flex items-center justify-center gap-6 text-xs text-secondary-300">
                <div className="flex items-center gap-2">
                    <div className="h-3 w-3 bg-primary/10"></div>
                    <span>{t('appointment.available')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="h-3 w-3 bg-primary"></div>
                    <span>{t('appointment.selected')}</span>
                </div>
            </div>
        </div>
    );
}

function buildAvailabilityIndex(raw: ServiceSlot[]): ServiceSlotAvailabilityIndex {
    const byDay: Record<string, ServiceSlotDaySlots> = {};

    raw.forEach((item, idx) => {
        const [datePart, timePart] = item.annahmezeitpunkt.split(' ');
        const [, endTime] = item.rueckgabezeitpunkt.split(' ');

        if (!datePart) {
            return;
        }
        if (!timePart) {
            return;
        }

        if (!byDay[datePart]) {
            byDay[datePart] = {
                date: datePart,
                slots: [],
            };
        }

        byDay[datePart].slots.push({
            id: `${datePart}-${timePart}-${idx}`,
            start: timePart.slice(0, 5),
            end: endTime?.slice(0, 5) || null,
            rawIndex: idx,
        });
    });

    const allDaysSorted = Object.keys(byDay).sort(); // ascending

    return { byDay, allDaysSorted };
}
